import {
  setInterfaceCoverage,
  setIsInterfaceCoverageLoading,
  setFamiliesOverviewByInterface,
  setIsFamiliesOverviewLoading,
  setBrandStepCoverage,
  setIsBrandStepCoverageLoading,
  setNeedsAttentionRows,
  setIsNeedsAttentionLoading,
} from "../../../features/familiesSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import { familiesServices } from "../../../services/api/families.endpoints";
import store from "../../../store";

async function getInterfaceCoverage(
  oauthTokenHeader: string,
  website_shared_uid: any,
  dispatch: any,
) {
  try {
    dispatch(setIsInterfaceCoverageLoading(true));
    const response = await familiesServices.getInterfaceCoverage(
      oauthTokenHeader,
      website_shared_uid,
    );

    if (response.status !== 200) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Something went wrong while fetching interface coverage",
          type: "error",
        }),
      );
      return;
    }

    const data = response?.data?.data?.query_output ?? [];
    const totals =
      data.find((row: any) => row.interface_name === "TOTAL") ?? null;
    const rows = data.filter((row: any) => row.interface_name !== "TOTAL");

    dispatch(setInterfaceCoverage({ rows, totals }));
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    dispatch(setIsInterfaceCoverageLoading(false));
  }
}

async function getFamiliesOverview(
  oauthTokenHeader: string,
  website_shared_uid: any,
  backend_interface_id: any,
  dispatch: any,
) {
  try {
    dispatch(setIsFamiliesOverviewLoading(true));
    const response = await familiesServices.getFamiliesOverview(
      oauthTokenHeader,
      website_shared_uid,
      backend_interface_id,
    );

    if (response.status !== 200) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Something went wrong while fetching families overview",
          type: "error",
        }),
      );
      return;
    }

    // Real shape: a flat query_output, one row per family (any step) on this
    // interface, with brands/things arriving as JSON-encoded strings.
    const rows = response?.data?.data?.query_output ?? [];
    const parseJsonField = (value: any) => {
      if (Array.isArray(value)) return value;
      if (typeof value !== "string") return [];
      try {
        return JSON.parse(value);
      } catch {
        return [];
      }
    };
    const families = rows.map((row: any) => ({
      ...row,
      brands: parseJsonField(row.brands),
      things: parseJsonField(row.things),
    }));

    const interfaceDetail = {
      backend_interface_id:
        families[0]?.backend_interface_id ?? backend_interface_id,
      backend_interface_name: families[0]?.backend_interface_name ?? null,
      steps: {
        fetcher: { families: families.filter((f: any) => f.step === "fetcher") },
        unifier: { families: families.filter((f: any) => f.step === "unifier") },
        advisor: { families: families.filter((f: any) => f.step === "advisor") },
      },
    };
    dispatch(setFamiliesOverviewByInterface(interfaceDetail));
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    dispatch(setIsFamiliesOverviewLoading(false));
  }
}

async function getBrandStepCoverage(
  oauthTokenHeader: string,
  website_shared_uid: any,
  dispatch: any,
) {
  try {
    dispatch(setIsBrandStepCoverageLoading(true));
    const response = await familiesServices.getBrandStepCoverage(
      oauthTokenHeader,
      website_shared_uid,
    );

    if (response.status !== 200) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Something went wrong while fetching brand step coverage",
          type: "error",
        }),
      );
      return;
    }

    const rows = response?.data?.data?.query_output ?? [];
    dispatch(setBrandStepCoverage(rows));
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    dispatch(setIsBrandStepCoverageLoading(false));
  }
}

async function getNeedsAttentionSummary(
  oauthTokenHeader: string,
  website_shared_uid: any,
  dispatch: any,
) {
  try {
    dispatch(setIsNeedsAttentionLoading(true));
    const response = await familiesServices.getNeedsAttentionSummary(
      oauthTokenHeader,
      website_shared_uid,
    );

    if (response.status !== 200) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Something went wrong while fetching needs attention summary",
          type: "error",
        }),
      );
      return;
    }

    const rows = response?.data?.data?.query_output ?? [];
    dispatch(setNeedsAttentionRows(rows));
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    dispatch(setIsNeedsAttentionLoading(false));
  }
}

// The candidate picker is still a placeholder (its ids look like "c1", not
// real backend ids) — the API correctly rejects non-integer map_ids, so this
// drops anything that isn't a real integer instead of sending it. Once the
// picker is backed by a real endpoint returning real numeric ids, this is a
// no-op passthrough.
function sanitizeMapIds(payload: any) {
  if (!payload || !Array.isArray(payload.map_ids)) return payload;
  const original = payload.map_ids;
  const ids = original.map((id: any) => Number(id)).filter((n: number) => Number.isInteger(n));
  const next = { ...payload };
  // A genuinely empty array (e.g. detaching the last real item) must still
  // be sent as `map_ids: []`. Only drop the key when filtering placeholder
  // junk out of a *non-empty* selection left nothing real behind.
  if (ids.length === 0 && original.length > 0) {
    delete next.map_ids;
  } else {
    next.map_ids = ids;
  }
  return next;
}

async function createFamily(
  oauthTokenHeader: string,
  payload: any,
  dispatch: any,
  successMessage: string = "Family created successfully",
): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await familiesServices.createFamily(
      oauthTokenHeader,
      sanitizeMapIds(payload),
    );
    if (response.status !== 200 && response.status !== 201) {
      const message = "Something went wrong while creating the family";
      dispatch(setDisplayErrorSnackBar({ message, type: "error" }));
      return { success: false, message };
    }
    dispatch(
      setDisplayErrorSnackBar({
        message: successMessage,
        type: "success",
      }),
    );
    return { success: true };
  } catch (e: any) {
    const message = e?.parsedText || "Unknown error occurred";
    dispatch(setDisplayErrorSnackBar({ message, type: "error" }));
    return { success: false, message };
  }
}

async function updateFamily(
  oauthTokenHeader: string,
  familyId: any,
  // The real API 400s with "step must be one of 'fetcher', 'unifier',
  // 'advisor'" on PUT if `step` is missing, even though it never actually
  // changes and the doc only documented { ui_family_name, map_ids }.
  // Requiring the caller to pass identity here keeps that fact in one place
  // instead of every call site having to remember to add it. Confirmed
  // against the team's Postman collection: the body is exactly
  // { step, ui_family_name, map_ids } — no backend_interface_id.
  identity: { step: any; backendInterfaceId?: any },
  payload: any,
  dispatch: any,
): Promise<boolean> {
  try {
    const fullPayload = {
      step: identity?.step,
      ...payload,
    };
    const response = await familiesServices.updateFamily(
      oauthTokenHeader,
      familyId,
      sanitizeMapIds(fullPayload),
    );
    if (response.status !== 200 && response.status !== 204) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Something went wrong while updating the family",
          type: "error",
        }),
      );
      return false;
    }
    dispatch(
      setDisplayErrorSnackBar({
        message: "Family updated successfully",
        type: "success",
      }),
    );
    return true;
  } catch (e: any) {
    dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return false;
  }
}

async function deleteFamily(
  oauthTokenHeader: string,
  urlId: any, // website_shared_uid — the URL id, not the family's own id
  identity: { step: any; familyId: any },
  dispatch: any,
): Promise<boolean> {
  try {
    const response = await familiesServices.deleteFamily(oauthTokenHeader, urlId, {
      step: identity?.step,
      id: identity?.familyId,
    });
    if (response.status !== 200 && response.status !== 204) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Something went wrong while deleting the family",
          type: "error",
        }),
      );
      return false;
    }
    dispatch(
      setDisplayErrorSnackBar({
        message: "Family deleted successfully",
        type: "success",
      }),
    );
    return true;
  } catch (e: any) {
    dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return false;
  }
}

// The real response shape for this check isn't finalized yet, so this reads
// defensively for any plausible "this breaks something" signal instead of a
// single named field, and fails open (treats the change as safe) if the
// endpoint itself errors — an unfinished/unavailable backend check shouldn't
// block the whole Edit flow.
async function checkFamiliesConnectability(
  oauthTokenHeader: string,
  payload: any,
): Promise<{ risky: boolean; message?: string }> {
  try {
    const response = await familiesServices.checkFamiliesConnectability(
      oauthTokenHeader,
      sanitizeMapIds(payload),
    );
    const data = response?.data?.data ?? response?.data ?? {};
    const affected = data.affected_brands ?? data.brands ?? [];
    const warnings = data.warnings ?? [];
    const risky = Boolean(
      data.is_breaking ||
        data.breaking ||
        data.risky ||
        (Array.isArray(affected) && affected.length > 0) ||
        (Array.isArray(warnings) && warnings.length > 0),
    );
    const message =
      data.message ||
      (Array.isArray(warnings) && warnings.length > 0 ? warnings.join(" ") : undefined);
    return { risky, message };
  } catch {
    return { risky: false };
  }
}

export const familiesEndPointLogic = {
  getInterfaceCoverage,
  getFamiliesOverview,
  getBrandStepCoverage,
  getNeedsAttentionSummary,
  createFamily,
  updateFamily,
  deleteFamily,
  checkFamiliesConnectability,
};
